/**
 * Route table for the KMMO meta UI (docs/design/ui-simplify.md §3.3):
 *
 *   lobby (root: +field the world, see below)  create  hero {tab?}  skills {tab?}
 *   forge {tab?, itemId?, slot?}  dungeons {tab?}  reward
 *
 * +field (docs/design/zones.md): there is no lobby screen any more. The root route is the world:
 * opening it (every "back to the lobby") asks the app to enter the character's map, or character
 * creation when there is no character. Town NPCs and the in-world menu keys open the other
 * screens as overlays over the world.
 *
 * Every old route id stays registered as an alias with default params (the in-run HUD menu keys
 * C N B J V K and the meta keys F U open them): hub → lobby, character → hero, inventory →
 * hero{gear}, gems → hero{gems}, talents → skills{talents}, temper → forge{enhance}, sets →
 * forge{sets}, codex → forge{craft}, vault → dungeons{weekly}, rank → dungeons{rank}.
 */
import type { MetaRouter, MetaScreenFactory } from '../context';
import { ROUTES } from '../router';
import { bindMetaBridge } from '../metaBridge';
import { rewardScreen } from './reward';
import { heroScreen } from './hero';
import { skillsScreen } from './skills';
import { forgeScreen } from './forge';
import { dungeonsScreen } from './dungeons';
import { createScreen } from './create';
import { gridScreen } from './grid'; // +grid

/** +field: the root route has no UI: it hands over to the world (main.ts `enterWorld`). */
const worldEntry: MetaScreenFactory = (ctx) => {
  const el = document.createElement('div');
  el.className = 'km-world-entry';
  queueMicrotask(() => {
    if (ctx.router.current()?.id === ROUTES.lobby) ctx.enterWorld?.();
  });
  return { el };
};

export function registerMetaScreens(router: MetaRouter): void {
  router.register(ROUTES.lobby, worldEntry, { title: 'World' });
  router.register(ROUTES.create, createScreen, { title: 'Create your hero' });
  router.register(ROUTES.reward, rewardScreen, { title: 'Run Rewards' });
  router.register(ROUTES.hero, heroScreen, { title: 'Hero' });
  router.register(ROUTES.skills, skillsScreen, { title: 'Skills' });
  router.register(ROUTES.forge, forgeScreen, { title: 'Forge' });
  router.register(ROUTES.dungeons, dungeonsScreen, { title: 'Dungeons' });
  router.register(ROUTES.grid, gridScreen, { title: 'Waystone Grid' }); // +grid

  router.registerAlias(ROUTES.hub, ROUTES.lobby);
  router.registerAlias(ROUTES.character, ROUTES.hero);
  router.registerAlias(ROUTES.inventory, ROUTES.hero, { tab: 'gear' });
  router.registerAlias(ROUTES.gems, ROUTES.hero, { tab: 'gems' });
  router.registerAlias(ROUTES.talents, ROUTES.grid); // +grid: talents are retired; N opens the Waystone Grid
  router.registerAlias(ROUTES.temper, ROUTES.forge, { tab: 'enhance' });
  router.registerAlias(ROUTES.sets, ROUTES.forge, { tab: 'sets' });
  router.registerAlias(ROUTES.codex, ROUTES.forge, { tab: 'craft' });
  router.registerAlias(ROUTES.vault, ROUTES.dungeons, { tab: 'weekly' });
  router.registerAlias(ROUTES.rank, ROUTES.dungeons, { tab: 'rank' });

  // HUD menu icons + hotkeys open these screens (as an overlay over the world or a key).
  bindMetaBridge(router);
}
